import { MatDialog } from '@angular/material/dialog';
import { CallbackOutcomeDialogComponent } from '../b2c-callback/callback-outcome-dialog.component';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { auditTime, filter, firstValueFrom, Subject, Subscription } from 'rxjs';
import {
  B2cCallbackRequest,
  Order,
  OrderLight,
  RingostatIncomingCallNotification,
  User
} from 'src/typings';
import { B2cCallbackService } from '../b2c-callback/b2c-callback.service';
import { OrderService } from '../order/order.service';
import { AppState } from '../redux/app.state';
import { RingostatCallService } from '../ringostat/ringostat-call.service';
import { NotificationService } from '../services/notification.service';
import { extractApiErrorMessage } from '../shared/utils/extract-api-error-message.util';
import { SupportService } from '../support.service';
import { CurrentUserService as UserService, UserOrderStatus } from '../user/user.service';
import { filterShellNavItems, SHELL_NAV_ITEMS, ShellNavItem } from './shell-nav.config';

type IncomingB2cOrder = {
  id: number;
  source?: 'CRM' | 'B2C' | 'PARTS_SELECTION';
  date?: Date | string;
  date_created?: Date | string;
  client?: string | { name?: string | null; surname?: string | null; phone?: string | null } | null;
  client_phone?: string | null;
  manager?: string | null;
  user?: User | null;
  is_canceled?: boolean;
  sum?: number | string | null;
  products?: Array<{ price?: number | string; quan?: number | string }>;
};

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, OnDestroy {
  user: User | undefined;

  navigationLinks: ShellNavItem[] = [];

  orderSubscription: Subscription;

  orderStatusSubscription: Subscription;

  orderUpdateSubscription: Subscription;

  storeSubscription: Subscription;

  routeSubscription: Subscription;

  ringostatIncomingCallSubscription: Subscription;

  ringostatCallFinishedSubscription: Subscription;

  b2cCallbackCreatedSubscription: Subscription;

  b2cCallbackUpdatedSubscription: Subscription;

  orderStatus: UserOrderStatus | undefined;

  tsdHeaderOpen = false;

  incomingB2cOrders: IncomingB2cOrder[] = [];

  incomingB2cBusyById: Record<number, boolean> = {};

  incomingB2cCallbacks: B2cCallbackRequest[] = [];

  incomingB2cCallbackBusyById: Record<number, boolean> = {};

  incomingWorkTrayTab: 'callbacks' | 'orders' = 'callbacks';

  activeRingostatCall: RingostatIncomingCallNotification | null = null;

  ringostatCallAlertMinimized = false;

  private activeRingostatCallTimerId: ReturnType<typeof setTimeout> | null = null;

  private readonly recentRingostatCallKeys = new Map<string, number>();

  private readonly ringostatCallDedupeMs = 30000;

  private readonly ringostatCallAlertDurationMs = 120000;

  private readonly incomingQueueSubscriptions = new Subscription();
  private readonly refreshIncomingOrders = new Subject<void>();
  private readonly refreshIncomingCallbacks = new Subject<void>();
  private incomingOrdersRevision = 0;
  private incomingCallbacksRevision = 0;
  private incomingOrdersLoad = 0;
  private incomingCallbacksLoad = 0;
  private destroyed = false;

  @ViewChild('drawer') drawer: MatDrawer | undefined;

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private orderService: OrderService,
    private b2cCallbackService: B2cCallbackService,
    private ringostatCallService: RingostatCallService,
    public supportService: SupportService,
    private router: Router,
    private notificationService: NotificationService,
    private dialog: MatDialog
  ) {
    this.incomingQueueSubscriptions.add(this.refreshIncomingOrders.pipe(auditTime(150))
      .subscribe(() => void this.loadIncomingB2cOrders()));
    this.incomingQueueSubscriptions.add(this.refreshIncomingCallbacks.pipe(auditTime(150))
      .subscribe(() => void this.loadIncomingB2cCallbacks()));
    this.incomingQueueSubscriptions.add(this.orderService.socketConnectedGW.subscribe(() => {
      this.refreshIncomingOrders.next();
      this.refreshIncomingCallbacks.next();
    }));
    this.incomingQueueSubscriptions.add(this.orderService.orderClaimedGW.subscribe((event) => {
      if (!event?.order_id) return;
      this.removeIncomingB2cOrder(Number(event.order_id));
    }));
    this.orderSubscription = this.orderService.newOrderGW.subscribe((order) => {
      this.orderService.newOrderNotification(order);
      this.mergeIncomingB2cOrder(order);
      this.refreshStatus();
    });
    this.orderUpdateSubscription = this.orderService.updateOrderGW.subscribe((order) => {
      this.handleIncomingB2cOrderUpdate(order);
    });
    this.b2cCallbackCreatedSubscription = this.b2cCallbackService.createdGW.subscribe((request) => {
      this.mergeIncomingB2cCallback(request);
      this.notifyIncomingB2cCallback(request);
    });
    this.b2cCallbackUpdatedSubscription = this.b2cCallbackService.updatedGW.subscribe((request) => {
      this.handleIncomingB2cCallbackUpdate(request);
    });
    this.orderStatusSubscription = this.orderService.orderStatusChangedGW
      .pipe(auditTime(1000))
      .subscribe(() => {
        void this.refreshStatus();
      });
    this.ringostatIncomingCallSubscription = this.ringostatCallService.incomingCallGW.subscribe(
      (notification) => this.handleRingostatIncomingCall(notification)
    );
    this.ringostatCallFinishedSubscription = this.ringostatCallService.callFinishedGW.subscribe(
      (notification) => this.handleRingostatCallFinished(notification)
    );
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.navigationLinks = filterShellNavItems(SHELL_NAV_ITEMS, this.user);
    });
    this.routeSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.tsdHeaderOpen = false;
        this.closeDrawer();
      });
  }

  get orderList() {
    return `В обработке : ${this.orderStatus?.proc.count} \n Собрано : ${this.orderStatus?.wh.count} \n Отказ : ${this.orderStatus?.decline.count} \n Завершено : ${this.orderStatus?.done.count}`;
  }

  get incomingB2cPreviewOrders() {
    return this.incomingB2cOrders;
  }

  get incomingB2cHiddenCount() {
    return Math.max(this.incomingB2cOrders.length - this.incomingB2cPreviewOrders.length, 0);
  }

  get incomingB2cCallbackPreviewItems() {
    return this.incomingB2cCallbacks;
  }

  get incomingB2cCallbackHiddenCount() {
    return Math.max(
      this.incomingB2cCallbacks.length - this.incomingB2cCallbackPreviewItems.length,
      0
    );
  }

  get isTsdRoute() {
    const url = this.router.url || '';
    return this.isTsdUrl(url);
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.refreshStatus(), this.loadIncomingB2cCallbacks(), this.loadIncomingB2cOrders()]);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.incomingQueueSubscriptions.unsubscribe();
    this.refreshIncomingOrders.complete();
    this.refreshIncomingCallbacks.complete();
    this.orderSubscription.unsubscribe();
    this.orderUpdateSubscription.unsubscribe();
    this.b2cCallbackCreatedSubscription.unsubscribe();
    this.b2cCallbackUpdatedSubscription.unsubscribe();
    this.orderStatusSubscription.unsubscribe();
    this.storeSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
    this.ringostatIncomingCallSubscription.unsubscribe();
    this.ringostatCallFinishedSubscription.unsubscribe();
    this.clearActiveRingostatCallTimer();
  }

  toggleDrawer() {
    this.drawer?.toggle();
  }

  closeDrawer() {
    this.drawer?.close();
  }

  toggleTsdHeader() {
    this.tsdHeaderOpen = !this.tsdHeaderOpen;
  }

  closeTsdHeader() {
    this.tsdHeaderOpen = false;
  }

  async refreshStatus() {
    this.orderStatus = await firstValueFrom(this.userService.getOrderStatus());
  }

  trackIncomingB2cOrder(_: number, order: IncomingB2cOrder) {
    return order.id;
  }

  trackIncomingB2cCallback(_: number, request: B2cCallbackRequest) {
    return request.id;
  }

  getIncomingB2cClientLabel(order: IncomingB2cOrder) {
    if (typeof order.client === 'string') {
      return order.client || 'Клиент без имени';
    }

    const clientName = [order.client?.name, order.client?.surname].filter(Boolean).join(' ').trim();
    return clientName || 'Клиент без имени';
  }

  getIncomingB2cPhoneLabel(order: IncomingB2cOrder) {
    if (order.client_phone) return order.client_phone;
    if (typeof order.client === 'object' && order.client?.phone) return order.client.phone;
    return '-';
  }

  getIncomingB2cSumLabel(order: IncomingB2cOrder) {
    return this.supportService.formatMoney(this.getIncomingB2cSum(order));
  }

  getIncomingB2cDateLabel(order: IncomingB2cOrder) {
    const value = order.date_created || order.date;
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getIncomingB2cCallbackNameLabel(request: B2cCallbackRequest) {
    return request.client_name || request.name || 'Клиент без имени';
  }

  getIncomingB2cCallbackPhoneLabel(request: B2cCallbackRequest) {
    return this.formatPhoneLabel(request.phone || request.client_phone || '');
  }

  getIncomingB2cCallbackPhoneHref(request: B2cCallbackRequest) {
    const digits = String(request.phone || request.client_phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 12 && digits.startsWith('38')) return `tel:+${digits}`;
    if (digits.length === 9) return `tel:+380${digits}`;
    return `tel:+38${digits}`;
  }

  getIncomingB2cCallbackDateLabel(request: B2cCallbackRequest) {
    return this.formatTimeLabel(request.date_created);
  }

  get activeRingostatClientLabel() {
    const call = this.activeRingostatCall;
    if (!call) return '';
    return call.clientName || (call.clientId ? `Клиент #${call.clientId}` : 'Клиент не найден');
  }

  get activeRingostatPhoneLabel() {
    return this.activeRingostatCall ? this.getRingostatPhoneLabel(this.activeRingostatCall) : '';
  }

  get activeRingostatReceivedAtLabel() {
    const value = this.activeRingostatCall?.receivedAt;
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  get canOpenActiveRingostatClient() {
    return Boolean(this.activeRingostatCall?.clientId);
  }

  get activeRingostatOwnerLabel() {
    const call = this.activeRingostatCall;
    if (!call?.clientId) return '';

    const ownerId = Number(call.ownerManagerUserId || 0);
    if (!ownerId) return 'Клиент без менеджера';
    if (ownerId === Number(this.user?.id || 0)) return 'Ваш клиент';

    return `Клиент менеджера: ${call.ownerManagerUserName || `#${ownerId}`}`;
  }

  get activeRingostatOwnerReasonLabel() {
    const labels: Record<string, string> = {
      recent_call_manager: 'недавний звонок',
      responsible_manager: 'ответственный менеджер',
      open_order_manager: 'открытый заказ',
      closed_order_history_manager: 'история заказов'
    };
    const reason = this.activeRingostatCall?.ownerReason || '';
    return labels[reason] || '';
  }

  get isActiveRingostatForeignClient() {
    const call = this.activeRingostatCall;
    if (!call?.clientId) return false;

    const ownerId = Number(call.ownerManagerUserId || 0);
    return Boolean(ownerId && ownerId !== Number(this.user?.id || 0));
  }

  async claimIncomingB2cOrder(order: IncomingB2cOrder, event?: Event) {
    event?.stopPropagation();
    if (this.incomingB2cBusyById[order.id]) return;

    this.incomingB2cBusyById = {
      ...this.incomingB2cBusyById,
      [order.id]: true
    };

    try {
      await firstValueFrom(this.orderService.claim(order.id));
      this.removeIncomingB2cOrder(order.id);
      this.notificationService.notice(`Заказ №${order.id} взят в обработку`);
      await this.refreshStatus();
    } catch (err) {
      console.error(err);
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось взять B2C-заказ в обработку')
      );
      await this.loadIncomingB2cOrders();
    } finally {
      const nextBusy = { ...this.incomingB2cBusyById };
      delete nextBusy[order.id];
      this.incomingB2cBusyById = nextBusy;
    }
  }

  async completeIncomingB2cCallback(request: B2cCallbackRequest, event?: Event) {
    event?.stopPropagation();
    if (this.incomingB2cCallbackBusyById[request.id]) return;

    this.incomingB2cCallbackBusyById = {
      ...this.incomingB2cCallbackBusyById,
      [request.id]: true
    };

    try {
      const result = await firstValueFrom(this.dialog.open(CallbackOutcomeDialogComponent, {
        width: '440px', maxWidth: '95vw'
      }).afterClosed());
      if (!result) return;
      const updated = await firstValueFrom(this.b2cCallbackService.complete(request.id, result));
      this.handleIncomingB2cCallbackUpdate(updated);
      this.notificationService.notice(result.outcome === 'NO_ANSWER'
        ? 'Нет ответа — заявка остаётся в очереди' : `Результат заявки №${request.id} сохранён`);
    } catch (err) {
      console.error(err);
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось закрыть заявку на перезвон')
      );
      await this.loadIncomingB2cCallbacks();
    } finally {
      const nextBusy = { ...this.incomingB2cCallbackBusyById };
      delete nextBusy[request.id];
      this.incomingB2cCallbackBusyById = nextBusy;
    }
  }

  openIncomingB2cCallbackClient(request: B2cCallbackRequest, event?: Event) {
    event?.stopPropagation();
    if (!request.client_id) return;
    this.router.navigateByUrl(`/client/profile/${request.client_id}`);
  }

  openActiveRingostatClient(event?: Event) {
    event?.stopPropagation();
    const clientId = Number(this.activeRingostatCall?.clientId || 0);
    if (!clientId) return;

    this.router.navigateByUrl(`/client/profile/${clientId}`);
  }

  closeActiveRingostatCall(event?: Event) {
    event?.stopPropagation();
    this.dismissActiveRingostatCall();
  }

  minimizeActiveRingostatCall(event?: Event) {
    event?.stopPropagation();
    this.ringostatCallAlertMinimized = true;
  }

  expandActiveRingostatCall(event?: Event) {
    event?.stopPropagation();
    this.ringostatCallAlertMinimized = false;
  }

  private isTsdUrl(url: string) {
    return url.startsWith('/warehouse/scanner') || url.startsWith('/docs/stocktaking-v2/scan');
  }

  private handleRingostatIncomingCall(notification: RingostatIncomingCallNotification) {
    if (!notification) {
      return;
    }

    if (!this.canShowRingostatIncomingCall(notification)) {
      if (this.shouldDismissActiveRingostatCall(notification)) {
        this.dismissActiveRingostatCall();
      }
      return;
    }

    if (
      this.activeRingostatCall &&
      this.isSameRingostatCall(this.activeRingostatCall, notification)
    ) {
      this.activeRingostatCall = {
        ...this.activeRingostatCall,
        ...notification
      };
      this.restartActiveRingostatCallTimer();
      return;
    }

    const dedupeKey = this.getRingostatCallKey(notification);
    const now = Date.now();
    const lastShownAt = this.recentRingostatCallKeys.get(dedupeKey) || 0;

    if (now - lastShownAt < this.ringostatCallDedupeMs) {
      return;
    }

    this.recentRingostatCallKeys.set(dedupeKey, now);
    this.pruneRingostatCallKeys(now);

    this.activeRingostatCall = notification;
    this.ringostatCallAlertMinimized = false;
    this.restartActiveRingostatCallTimer();
  }

  private handleRingostatCallFinished(notification: RingostatIncomingCallNotification) {
    if (!notification || !this.activeRingostatCall) {
      return;
    }

    if (!this.isSameRingostatCall(this.activeRingostatCall, notification)) {
      return;
    }

    this.dismissActiveRingostatCall();
  }

  private canShowRingostatIncomingCall(notification: RingostatIncomingCallNotification) {
    const managerUserId = Number(notification.managerUserId || 0);
    const currentUserId = Number(this.user?.id || 0);
    if (managerUserId) {
      return Boolean(currentUserId && managerUserId === currentUserId);
    }

    const visibleUserIds = this.getRingostatVisibleUserIds(notification);
    if (visibleUserIds) {
      return Boolean(
        currentUserId &&
          notification.clientId &&
          visibleUserIds.some((userId) => userId === currentUserId)
      );
    }

    return false;
  }

  private shouldDismissActiveRingostatCall(notification: RingostatIncomingCallNotification) {
    if (
      !this.activeRingostatCall ||
      !this.isSameRingostatCall(this.activeRingostatCall, notification)
    ) {
      return false;
    }

    const managerUserId = Number(notification.managerUserId || 0);
    const currentUserId = Number(this.user?.id || 0);
    if (managerUserId) {
      return !currentUserId || managerUserId !== currentUserId;
    }

    const visibleUserIds = this.getRingostatVisibleUserIds(notification);
    // Событие без менеджера и без адресатов — информационное, оно не должно
    // закрывать плашку у менеджера, которому сейчас звонит цепочка.
    if (!visibleUserIds || !visibleUserIds.length) return false;

    return !currentUserId || !visibleUserIds.some((userId) => userId === currentUserId);
  }

  private getRingostatVisibleUserIds(notification: RingostatIncomingCallNotification) {
    if (!Array.isArray(notification.visibleUserIds)) return null;

    return notification.visibleUserIds
      .map((userId) => Number(userId || 0))
      .filter((userId) => Number.isInteger(userId) && userId > 0);
  }

  private getRingostatCallKey(notification: RingostatIncomingCallNotification) {
    return (
      notification.callId ||
      [
        notification.normalizedCaller || notification.caller || notification.clientPhone || '',
        notification.clientId || '',
        notification.managerUserId || ''
      ].join(':')
    );
  }

  private isSameRingostatCall(
    activeCall: RingostatIncomingCallNotification,
    finishedCall: RingostatIncomingCallNotification
  ) {
    const activeCallId = String(activeCall.callId || '').trim();
    const finishedCallId = String(finishedCall.callId || '').trim();

    if (activeCallId && finishedCallId) {
      return activeCallId === finishedCallId;
    }

    const activePhone = this.getRingostatComparablePhone(activeCall);
    const finishedPhone = this.getRingostatComparablePhone(finishedCall);
    if (activePhone && finishedPhone && activePhone === finishedPhone) {
      return true;
    }

    return Boolean(
      activeCall.clientId &&
        finishedCall.clientId &&
        Number(activeCall.clientId) === Number(finishedCall.clientId)
    );
  }

  private getRingostatComparablePhone(notification: RingostatIncomingCallNotification) {
    const value = notification.clientPhone || notification.normalizedCaller || notification.caller;
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 12 && digits.startsWith('38')) return digits.slice(2);
    return digits;
  }

  private pruneRingostatCallKeys(now: number) {
    this.recentRingostatCallKeys.forEach((shownAt, key) => {
      if (now - shownAt > this.ringostatCallDedupeMs * 3) {
        this.recentRingostatCallKeys.delete(key);
      }
    });
  }

  private getRingostatPhoneLabel(notification: RingostatIncomingCallNotification) {
    const value = notification.clientPhone || notification.caller || notification.normalizedCaller;
    if (!value) {
      return '';
    }

    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10 && digits.startsWith('0')) {
      return `+38${digits}`;
    }
    if (digits.length === 12 && digits.startsWith('38')) {
      return `+${digits}`;
    }
    return String(value);
  }

  private restartActiveRingostatCallTimer() {
    this.clearActiveRingostatCallTimer();
    this.activeRingostatCallTimerId = setTimeout(() => {
      this.activeRingostatCall = null;
      this.ringostatCallAlertMinimized = false;
      this.activeRingostatCallTimerId = null;
    }, this.ringostatCallAlertDurationMs);
  }

  private clearActiveRingostatCallTimer() {
    if (!this.activeRingostatCallTimerId) return;
    clearTimeout(this.activeRingostatCallTimerId);
    this.activeRingostatCallTimerId = null;
  }

  private dismissActiveRingostatCall() {
    this.activeRingostatCall = null;
    this.ringostatCallAlertMinimized = false;
    this.clearActiveRingostatCallTimer();
  }

  private async loadIncomingB2cOrders() {
    const load = ++this.incomingOrdersLoad;
    const revision = this.incomingOrdersRevision;
    try {
      const response = await firstValueFrom(this.orderService.getIncomingB2cOrders());
      if (this.destroyed || load !== this.incomingOrdersLoad) return;
      // A response started before a socket update must not restore a claimed order.
      if (revision !== this.incomingOrdersRevision) {
        this.refreshIncomingOrders.next();
        return;
      }
      this.incomingB2cOrders = (response.data || [])
        .filter((order) => this.isIncomingB2cOrder(order))
        .map((order) => this.normalizeIncomingB2cOrder(order))
        .sort((left, right) => this.compareIncomingB2cOrders(left, right))
        .slice(0, 20);
    } catch (err) {
      console.error(err);
    }
  }

  private async loadIncomingB2cCallbacks() {
    const load = ++this.incomingCallbacksLoad;
    const revision = this.incomingCallbacksRevision;
    try {
      const response = await firstValueFrom(this.b2cCallbackService.getPending({ limit: 20 }));
      if (this.destroyed || load !== this.incomingCallbacksLoad) return;
      if (revision !== this.incomingCallbacksRevision) {
        this.refreshIncomingCallbacks.next();
        return;
      }
      this.incomingB2cCallbacks = (response.data || [])
        .filter((request) => this.isIncomingB2cCallback(request))
        .map((request) => this.normalizeIncomingB2cCallback(request))
        .sort((left, right) => this.compareIncomingB2cCallbacks(left, right))
        .slice(0, 20);
    } catch (err) {
      console.error(err);
    }
  }

  private handleIncomingB2cOrderUpdate(order: Order) {
    if (!order?.id) return;

    if (this.isIncomingB2cOrder(order)) {
      this.mergeIncomingB2cOrder(order);
      return;
    }

    this.removeIncomingB2cOrder(order.id);
  }

  private handleIncomingB2cCallbackUpdate(request: B2cCallbackRequest) {
    if (!request?.id) return;

    if (this.isIncomingB2cCallback(request)) {
      this.mergeIncomingB2cCallback(request);
      return;
    }

    this.removeIncomingB2cCallback(request.id);
  }

  private mergeIncomingB2cOrder(order: Order | OrderLight) {
    if (!this.isIncomingB2cOrder(order)) return;

    this.incomingOrdersRevision++;
    const normalizedOrder = this.normalizeIncomingB2cOrder(order);
    this.incomingB2cOrders = [
      normalizedOrder,
      ...this.incomingB2cOrders.filter((item) => item.id !== normalizedOrder.id)
    ]
      .sort((left, right) => this.compareIncomingB2cOrders(left, right))
      .slice(0, 20);
  }

  private removeIncomingB2cOrder(orderId: number) {
    this.incomingOrdersRevision++;
    const refill = this.incomingB2cOrders.length === 20 && this.incomingB2cOrders.some(order => order.id === orderId);
    this.incomingB2cOrders = this.incomingB2cOrders.filter((order) => Number(order.id) !== Number(orderId));
    if (refill) this.refreshIncomingOrders.next();
  }

  private mergeIncomingB2cCallback(request: B2cCallbackRequest) {
    if (!this.isIncomingB2cCallback(request)) return;

    this.incomingCallbacksRevision++;
    const normalizedRequest = this.normalizeIncomingB2cCallback(request);
    this.incomingB2cCallbacks = [
      normalizedRequest,
      ...this.incomingB2cCallbacks.filter((item) => item.id !== normalizedRequest.id)
    ]
      .sort((left, right) => this.compareIncomingB2cCallbacks(left, right))
      .slice(0, 20);
  }

  private removeIncomingB2cCallback(requestId: number) {
    this.incomingCallbacksRevision++;
    const refill = this.incomingB2cCallbacks.length === 20 && this.incomingB2cCallbacks.some(request => request.id === requestId);
    this.incomingB2cCallbacks = this.incomingB2cCallbacks.filter(
      (request) => Number(request.id) !== Number(requestId)
    );
    if (refill) this.refreshIncomingCallbacks.next();
  }

  private isIncomingB2cOrder(order: IncomingB2cOrder | Order | OrderLight) {
    return (
      order?.source === 'B2C' &&
      !order.is_canceled &&
      !('user' in order && order.user) &&
      !('manager' in order && order.manager)
    );
  }

  private isIncomingB2cCallback(request: B2cCallbackRequest) {
    return request?.status === 'NEW';
  }

  private normalizeIncomingB2cOrder(
    order: IncomingB2cOrder | Order | OrderLight
  ): IncomingB2cOrder {
    const rawSum = 'sum' in order ? Number(order.sum || 0) : this.getIncomingB2cSum(order);
    return {
      ...order,
      sum: Number.isFinite(rawSum) ? rawSum : 0
    };
  }

  private compareIncomingB2cOrders(left: IncomingB2cOrder, right: IncomingB2cOrder) {
    return this.getIncomingB2cTime(right) - this.getIncomingB2cTime(left);
  }

  private normalizeIncomingB2cCallback(request: B2cCallbackRequest): B2cCallbackRequest {
    return {
      ...request,
      date_created:
        request.date_created instanceof Date ? request.date_created : new Date(request.date_created)
    };
  }

  private compareIncomingB2cCallbacks(left: B2cCallbackRequest, right: B2cCallbackRequest) {
    return this.getIncomingB2cCallbackTime(left) - this.getIncomingB2cCallbackTime(right);
  }

  private getIncomingB2cTime(order: IncomingB2cOrder) {
    const value = order.date_created || order.date;
    if (!value) return order.id;

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? order.id : date.getTime();
  }

  private getIncomingB2cCallbackTime(request: B2cCallbackRequest) {
    const value = request.date_created;
    if (!value) return request.id;

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? request.id : date.getTime();
  }

  private getIncomingB2cSum(order: IncomingB2cOrder | Order) {
    const directSum = 'sum' in order ? Number(order.sum || 0) : NaN;
    if (Number.isFinite(directSum) && directSum > 0) return directSum;

    return (order.products || []).reduce(
      (total, product) => total + Number(product.price || 0) * Number(product.quan || 0),
      0
    );
  }

  private notifyIncomingB2cCallback(request: B2cCallbackRequest) {
    this.orderService.playSound();
    const actionName = request.client_id ? 'Открыть клиента' : 'Понятно';
    const snackRef = this.notificationService.warningWithAction(
      `Перезвонить: ${this.getIncomingB2cCallbackPhoneLabel(request)}`,
      actionName,
      { duration: 12000 }
    );

    snackRef.onAction().subscribe(() => {
      if (request.client_id) {
        this.router.navigateByUrl(`/client/profile/${request.client_id}`);
      }
    });
  }

  private formatPhoneLabel(value: string) {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '-';
    if (digits.length === 12 && digits.startsWith('38')) return `+${digits}`;
    if (digits.length === 9) return `+380${digits}`;
    return `+38${digits}`;
  }

  private formatTimeLabel(value?: Date | string | null) {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
